import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { ArrowCounterClockwise, DownloadSimple, PenNib } from "@phosphor-icons/react";
import { Input, Label } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

import { SectionOptions } from "./shared/section-options";

export const SignatureSection = () => {
  const id = "signature";

  const section = useResumeStore((state) => {
    try {
      if (!state.resume.data.sections) return null;
      return state.resume.data.sections.signature || null;
    } catch {
      return null;
    }
  });

  const fullName = useResumeStore((state) => {
    try {
      return state.resume.data.basics.name || "";
    } catch {
      return "";
    }
  });

  const setValue = useResumeStore((state) => state.setValue);
  const addItem = useResumeStore((state) => state.addItem);

  useEffect(() => {
    if (!section?.items.length) {
      try {
        addItem(id, { id: createId(), content: "", visible: true });
      } catch {
        // silent
      }
    }
  }, [section, addItem, id]);

  const signatureItem = section?.items[0] || { content: "", visible: true };
  const signatureContent = signatureItem.content || "";

  const [name, setName] = useState(signatureContent);

  useEffect(() => {
    if (signatureContent) {
      setName(signatureContent);
    } else if (fullName && !signatureContent) {
      const formattedName = fullName.replace(/\b\w/g, (c) => c.toUpperCase());
      setName(formattedName);
      if (section?.items[0]) {
        setValue(`sections.signature.items[0].content`, formattedName);
      }
    }
  }, [signatureContent, fullName, section, setValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\b\w/g, (c) => c.toUpperCase());
    setName(val);
    if (section?.items[0]) {
      setValue(`sections.signature.items[0].content`, val);
    }
  };

  const handleClear = () => {
    setName("");
    if (section?.items[0]) {
      setValue(`sections.signature.items[0].content`, "");
    }
  };

  const handleDownload = () => {
    if (!name) return;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = 800;
    canvas.height = 200;
    ctx.fillStyle = "#268599";
    ctx.font = `48px "Bastliga One", cursive`;
    ctx.fillText(name, 10, 100);
    const link = document.createElement("a");
    link.download = "signature.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  if (!section) return null;

  const labelClass = "text-[13px] font-medium text-[var(--fg-2,#404040)]";
  const inputClass =
    "border-[var(--border,#e5e5e5)] bg-white text-[var(--fg-1,#171717)] placeholder:text-[var(--fg-4,#a3a3a3)] focus-visible:border-[var(--accent,#268599)] focus-visible:ring-1 focus-visible:ring-[var(--accent,#268599)] transition-all";

  return (
    <section id={id} className="flex h-full flex-col overflow-y-auto">
      {/* Header */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
          <PenNib size={18} className="text-[var(--fg-2,#404040)]" />
        </div>
        <h2 className="flex-1 text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
          {section.name}
        </h2>
        <div className="shrink-0">
          <SectionOptions id={id} />
        </div>
      </header>

      {/* Subtitle */}
      <p className="px-6 py-3 text-sm text-[var(--fg-3,#737373)]">
        {t`Add a handwritten-style signature to the bottom of your resume. English letters only.`}
      </p>

      {/* Card */}
      <div className="mx-5 mb-5 rounded-xl border border-[var(--border,#e5e5e5)] bg-white">
        <main className={cn("flex flex-col gap-5 p-5", !section.visible && "opacity-50")}>

          {/* Name input */}
          <div className="space-y-1.5">
            <Label htmlFor="signature.content" className={labelClass}>
              {t`Your name`}
            </Label>
            <Input
              id="signature.content"
              type="text"
              value={name}
              placeholder={t`Type your name`}
              className={inputClass}
              onChange={handleChange}
            />
          </div>

          {/* Preview */}
          <div className="flex min-h-[90px] items-center justify-center rounded-xl border border-dashed border-[var(--border,#e5e5e5)] bg-gray-50">
            {name ? (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="signature px-4 text-[var(--accent,#268599)]"
              >
                {name}
              </motion.div>
            ) : (
              <p className="text-sm text-[var(--fg-4,#a3a3a3)]">
                {t`Your signature will appear here`}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={!name}
              onClick={handleClear}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--border,#e5e5e5)] bg-white px-3 py-1.5 text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowCounterClockwise size={14} />
              {t`Clear`}
            </button>
            <button
              type="button"
              disabled={!name}
              onClick={handleDownload}
              className="flex items-center gap-1.5 rounded-lg bg-[var(--accent,#268599)] px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-[var(--accent,#268599)]/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <DownloadSimple size={14} />
              {t`Download PNG`}
            </button>
          </div>

        </main>
      </div>

      <style>{`
        @font-face {
          font-family: 'Bastliga One';
          src: url('/fonts/signature/Bastliga-One.woff') format('woff'),
               url('/fonts/signature/Bastliga-One.ttf') format('truetype');
          font-display: swap;
        }
        .signature {
          font-family: 'Bastliga One', cursive !important;
          font-size: 48px !important;
        }
      `}</style>
    </section>
  );
};
